import { useCallback, useMemo } from 'react';
import { UiIconName } from '@cbhq/cds-icons';
import { Icon } from '@cbhq/cds-web/icons';
import { Box, Grid, HStack, VStack } from '@cbhq/cds-web/layout';
import { Pressable } from '@cbhq/cds-web/system';
import { Tag } from '@cbhq/cds-web/tag/Tag';
import { TextBody, TextHeadline, TextLabel2, TextTitle3 } from '@cbhq/cds-web/typography';

import { ReactComponent as LayerThreeSvg } from ':cloud/assets/Products/SmallLayerThree.svg';
import { useProject } from ':cloud/contexts/ProjectContext';
import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { useGetBaseL3 } from ':cloud/queries/Base/useGetBaseL3';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { handleDownloadJson } from ':cloud/utils/files';
import { openUrlInNewWindow } from ':cloud/utils/openUrlInNewWindow';
import { BaseL3Details } from ':cloud/widgets/base/BaseL3Details';
import { BaseL3EmptyState } from ':cloud/widgets/base/BaseL3EmptyState';
import { BaseL3RPCEndpointSection } from ':cloud/widgets/base/BaseL3RPCEndpoint';
import { Layout } from ':cloud/widgets/layout';
import { MAIN_CONTAINER_MIN_WIDTH } from ':cloud/widgets/product/constants';
import { CloudErrorBoundary } from ':cloud/widgets/sharedcomponents';

const TESTNET_NETWORK = 'base-sepolia';

type FileItemProps = {
  title: string;
  onPress?: () => void;
  iconName: UiIconName;
  label: string;
};

function FileItem({ title, onPress, iconName, label }: FileItemProps) {
  return (
    <VStack gap={1}>
      <TextHeadline as="p">{title}</TextHeadline>
      <Pressable background="transparent" noScaleOnPress onPress={onPress}>
        <HStack alignItems="center" gap={1}>
          <TextBody as="p" color="foregroundMuted">
            {label}
          </TextBody>
          <Icon name={iconName} size="s" color="foregroundMuted" />
        </HStack>
      </Pressable>
    </VStack>
  );
}

export function BaseL3Landing() {
  const { activeOrg } = useGetUser();
  const orgId = activeOrg?.organizationId;
  const { selectedProject } = useProject();

  const { isDesktop } = useSimpleBreakpoints();

  const responsiveTemplateColumns = useMemo(() => {
    return isDesktop ? '2fr 1fr' : '1fr';
  }, [isDesktop]);

  const { chain, isLoading } = useGetBaseL3({ orgId, projectId: selectedProject?.id });

  const isDeploying = chain?.status === 'pending';

  const handleDownload = useCallback((url?: string) => {
    return () => {
      if (!url) return;
      handleDownloadJson(url);
    };
  }, []);

  return (
    <CloudErrorBoundary name="base-l3">
      <Layout enableMaxWidth={false}>
        <Layout.MainContainer
          minWidth={MAIN_CONTAINER_MIN_WIDTH}
          spacingHorizontal={0}
          spacingTop={0}
          spacingBottom={0}
          fullWidth
        >
          {!chain && !isLoading && <BaseL3EmptyState />}
          {chain && (
            <VStack height="100vh">
              <VStack borderedBottom gap={6}>
                <HStack
                  alignItems="center"
                  gap={2}
                  spacingTop={5}
                  spacingHorizontal={7}
                  spacingBottom={0}
                >
                  <TextTitle3 as="h1">{chain?.name}</TextTitle3>
                  {chain?.network === TESTNET_NETWORK && <Tag colorScheme="yellow">Testnet</Tag>}
                </HStack>
                <BaseL3Details chain={chain} isDeploying={isDeploying} />
              </VStack>
              {isDeploying && (
                <VStack alignItems="center" justifyContent="center" spacingTop={10} gap={1}>
                  <Box spacingTop={10}>
                    <LayerThreeSvg />
                  </Box>
                  <TextTitle3 as="h1">Deploying your L3...</TextTitle3>
                  <TextLabel2 as="p" color="foregroundMuted">
                    The deployment may take up to 15 minutes
                  </TextLabel2>
                </VStack>
              )}
              {!isDeploying && (
                <Box width="100%" flexGrow={1}>
                  <Grid width="100%" templateColumns={responsiveTemplateColumns}>
                    <VStack spacing={5} spacingStart={7} gap={6}>
                      {chain?.resources?.rpcUrl && (
                        <BaseL3RPCEndpointSection title="RPC" url={chain?.resources?.rpcUrl} />
                      )}
                      {chain?.resources?.wsUrl && (
                        <BaseL3RPCEndpointSection title="WS" url={chain?.resources?.wsUrl} />
                      )}
                    </VStack>
                    <VStack borderedStart spacing={5} gap={4}>
                      <TextHeadline as="p">Quick links and files</TextHeadline>
                      <VStack gap={4}>
                        <FileItem
                          title="Block explorer"
                          label="blockscout"
                          iconName="diagonalUpArrow"
                          onPress={() =>
                            openUrlInNewWindow(chain?.resources?.blockExplorerUrl || '')
                          }
                        />
                        <FileItem
                          title="Contract addresses"
                          label="deployed.json"
                          iconName="download"
                          onPress={handleDownload(chain?.resources?.files?.deployed)}
                        />
                        <FileItem
                          title="Rollup config"
                          label="rollup.json"
                          iconName="download"
                          onPress={handleDownload(chain?.resources?.files?.rollupJson)}
                        />
                        <FileItem
                          title="Genesis JSON"
                          label="genesis.json"
                          iconName="download"
                          onPress={handleDownload(chain?.resources?.files?.genesisJson)}
                        />
                      </VStack>
                    </VStack>
                  </Grid>
                </Box>
              )}
            </VStack>
          )}
        </Layout.MainContainer>
      </Layout>
    </CloudErrorBoundary>
  );
}
